import React, {createContext, useContext, useEffect, useRef, useState} from 'react';

import {getConnectionFlowSettings} from './connectionContentService';
import useIsConnectionProcessPaused from './hooks/useIsConnectionProcessPaused';

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const ConnectionContext = createContext();

export const useConnectionContext = () => {
    const context = useContext(ConnectionContext);

    if (!context) {
        throw new Error('useConnectionContext must be used within a ConnectionProvider');
    }
    return context;
};

export const ConnectionProvider = ({children}) => {
    const connectionFlowSettings = getConnectionFlowSettings();
    const isPaused = useIsConnectionProcessPaused();

    const [connectionState, setConnectionState] = useState({
        isConnectionFinished: false,
        isQsgOpened: false,
        progress: 0,
        remainingTime: 0,
        currentStep: 0,
        steps: connectionFlowSettings,
    });

    const animationFrameRef = useRef(null);
    const animationStartTimeRef = useRef(null);
    const animationStartProgressRef = useRef(0);
    const animationTargetProgressRef = useRef(0);
    const currentProgressRef = useRef(0);
    const pausedTimeRef = useRef(null);

    const updateConnectionState = (newState) => {
        setConnectionState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    // Update remaining time every second
    useEffect(() => {
        const interval = setInterval(() => {
            if (isPaused) return;

            const futureTime = connectionState.steps.slice(connectionState.currentStep + 1).reduce((a, b) => a + b, 0);

            setConnectionState((prev) => ({
                ...prev,
                remainingTime: Math.max(prev.remainingTime - 1, futureTime, 1),
            }));
        }, 1000);

        return () => clearInterval(interval);
    }, [connectionState.currentStep, connectionState.steps, isPaused]);

    // When steps or currentStep change, set up new animation target
    useEffect(() => {
        if (!connectionState.steps?.length || connectionState.currentStep >= connectionState.steps.length) return;

        const totalTime = connectionState.steps.reduce((a, b) => a + b, 0);
        const futureTime = connectionState.steps.slice(connectionState.currentStep).reduce((a, b) => a + b, 0);
        const stepDuration = connectionState.steps[connectionState.currentStep] * 1000; // ms

        setConnectionState((prev) => ({
            ...prev,
            remainingTime: futureTime,
        }));

        const targetProgress = clamp(
            ((totalTime - futureTime + connectionState.steps[connectionState.currentStep]) / totalTime) * 99,
            0,
            99
        );

        // Animate from currentProgressRef (live value) to new target
        const startProgress = currentProgressRef.current;

        animationStartTimeRef.current = performance.now();
        animationStartProgressRef.current = startProgress;
        animationTargetProgressRef.current = targetProgress;

        const animate = (now) => {
            if (isPaused) {
                if (!pausedTimeRef.current) {
                    pausedTimeRef.current = now;
                }
                animationFrameRef.current = requestAnimationFrame(animate);
                return;
            }

            if (pausedTimeRef.current) {
                const pauseDuration = now - pausedTimeRef.current;

                animationStartTimeRef.current += pauseDuration;
                pausedTimeRef.current = null;
            }

            const elapsed = now - animationStartTimeRef.current;
            const t = clamp(elapsed / stepDuration, 0, 1);

            const nextProgress =
                animationStartProgressRef.current +
                (animationTargetProgressRef.current - animationStartProgressRef.current) * t;

            currentProgressRef.current = nextProgress;
            setConnectionState((prev) => ({
                ...prev,
                progress: nextProgress,
            }));

            if (t < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            } else {
                currentProgressRef.current = targetProgress;
                setConnectionState((prev) => ({
                    ...prev,
                    progress: targetProgress,
                }));
            }
        };

        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(animationFrameRef.current);
        };
    }, [connectionState.steps, connectionState.currentStep, isPaused]);

    return (
        <ConnectionContext.Provider value={{connectionState, updateConnectionState}}>
            {children}
        </ConnectionContext.Provider>
    );
};
